import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { generateSessionSettingsQueryOptions } from '@/hooks/sessions/use-session-settings';
import type { ConversationType } from '@/models/conversations/conversation-session';
import type { RespondedType, ResponseError } from '@/models/response';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { generateSessionKey } from '@/utils/session';
import { allChatSessionQueryOptions } from './use-conversation-sessions';

export default function useUpdateSessionDnd(type: ConversationType, id: Nullable<number>) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, boolean>({
    mutationKey: ['/UpdateSessionDoNotDisturb'],
    mutationFn: (params) =>
      fetcher('/UpdateSessionDoNotDisturb', {
        [type === 'session' ? 'ChatSessionID' : 'GroupID']: id,
        IsNotDisturb: Number(params),
      }),
    onSuccess: (_, params) => {
      if (!id) return;
      const key = generateSessionKey(type, id);

      useConversationSessionStore.getState().updateSession(key, { IsNotDisturb: params });

      queryClient.setQueryData(generateSessionSettingsQueryOptions(type, id).queryKey, (state) =>
        produce(state, (draft) => {
          if (draft) draft.IsNotDisturb = params;
        }),
      );

      queryClient.invalidateQueries(allChatSessionQueryOptions);
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}
